import { useEffect, useState, useCallback } from "react";
import { useInstitution } from "../context/InstitutionContext";
import { getInstitutionTheme, DEFAULT_THEME } from "../services/preferences";
import { hasValidToken } from "../utils/cookies";

/**
 * Hook para gerenciar o tema da instituição (cores e logo)
 * Aplica as cores como variáveis CSS no :root
 * Retorna o logo customizado (ou null para usar padrão)
 */
export const useInstitutionTheme = () => {
  const { institution } = useInstitution();
  const [logo, setLogo] = useState(null);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [loading, setLoading] = useState(true);

  const applyThemeColors = useCallback((themeData) => {
    const root = document.documentElement;
    
    // Aplica cor primária
    root.style.setProperty(
      "--primary-color",
      themeData?.primaryColor || DEFAULT_THEME.primaryColor
    );
    
    // Aplica cor secundária
    root.style.setProperty(
      "--secondary-color",
      themeData?.secondaryColor || DEFAULT_THEME.secondaryColor
    );

    // Calcula e aplica variação escura da cor primária para hover
    const primaryColor = themeData?.primaryColor || DEFAULT_THEME.primaryColor;
    root.style.setProperty(
      "--primary-color-dark",
      darkenColor(primaryColor, 15)
    );
  }, []);

  const loadTheme = useCallback(async () => {
    if (!institution?.id || !hasValidToken()) {
      // Se não há instituição ou token válido, aplica tema padrão
      applyThemeColors(DEFAULT_THEME);
      setTheme(DEFAULT_THEME);
      setLogo(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const themeData = await getInstitutionTheme();
      
      // Aplica cores CSS
      applyThemeColors(themeData);
      
      // Atualiza estado do tema
      setTheme(themeData);
      
      // Atualiza logo (null = usa padrão)
      setLogo(themeData?.logo || null);
    } catch (error) {
      console.error("Erro ao carregar tema da instituição:", error);
      // Em caso de erro, aplica tema padrão
      applyThemeColors(DEFAULT_THEME);
      setTheme(DEFAULT_THEME);
      setLogo(null);
    } finally {
      setLoading(false);
    }
  }, [institution?.id, applyThemeColors]);

  useEffect(() => {
    loadTheme();
  }, [loadTheme]);

  return { 
    logo, 
    theme, 
    loading, 
    refreshTheme: loadTheme 
  };
};

/**
 * Função auxiliar para escurecer uma cor hexadecimal
 * @param {string} hex - Cor em formato hexadecimal (#RRGGBB)
 * @param {number} percent - Porcentagem para escurecer (0-100)
 * @returns {string} Cor escurecida em formato hexadecimal
 */
const darkenColor = (hex, percent) => {
  // Remove o # se existir
  const color = hex.replace("#", "");
  
  // Converte para RGB
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);
  
  // Calcula os valores escurecidos
  const factor = (100 - percent) / 100;
  const newR = Math.round(r * factor);
  const newG = Math.round(g * factor);
  const newB = Math.round(b * factor);
  
  // Converte de volta para hexadecimal
  const toHex = (n) => n.toString(16).padStart(2, "0");
  
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};

export default useInstitutionTheme;
